import React, { useContext, useMemo } from 'react';
import { reach, Schema } from 'yup';

const SchemaContext = React.createContext<Schema<any>>(null as any);

export const SchemaProvider = SchemaContext.Provider;

/**
 * Use the current schema in context. Optionally takes an path argument
 * that resolves sub-schemas, e.g. 'user.name'
 */
export function useSchema(path = '') {
  const schema = useContext(SchemaContext);

  return useMemo(() => reach(schema, path), [path, schema]);
}

/**
 * Resolve the schema description of the given path.
 */
export function useSchemaDescription(path = '') {
  const schema = useSchema(path);
  return useMemo(() => schema.describe(), [schema]);
}

/**
 * Resolve the schema field properties of the given field name/path.
 */
export function useFieldSchema(name: string) {
  const description = useSchemaDescription(name);

  return useMemo(() => {
    return {
      required: description.tests.map(test => test.name).includes('required'),
      label: description.label || name
    };
  }, [name, description]);
}

/**
 * Resolve the schema meta information of the given path.
 */
export function useSchemaMeta<T extends object>(path = '') {
  const description = useSchemaDescription(path);

  return useMemo(() => {
    return description.meta as T;
  }, [description]);
}
